<template>
  <div
    v-if="plan"
    class="item"
    :class="{
      'item--selected': selectedPlanId === plan.id,
      'item--allowToClick': !showSelectPlanButton && handlePlanSelect,
      [`item_${plan.id}`]: true,
    }"
    @click="!showSelectPlanButton && handlePlanSelect && handlePlanSelect(plan)"
  >
    <div class="row row__first">
      <div class="header-row mb-8">
        <div class="header-container header-container--name">
          <div class="name-text">
            {{ plan.name }}
          </div>
        </div>
        <div
          v-if="selectedPlanId === plan.id"
          class="header-container header-container--selected"
        >
          <ui-icon size="12" :color="colors.white" icon="fas fa-check"></ui-icon>
        </div>
      </div>

      <div v-if="plan.id === 'basic' || plan.id === 'free'" class="item-price-basic">
        <div class="row-price">
          <span class="row-price-value">
            <span class="row-price-value">$ 0</span>
            <span class="row-price-unit"> / {{ t('mo') }} </span>
          </span>
        </div>
      </div>
      <template v-else>
        <div class="row-price">
          <span class="row-price-value"
            >$ {{ numeral(plan.price / 100).format('0,0[.]00') }}</span
          >
          <span class="row-price-unit"> / {{ t('mo') }} </span>
        </div>

        <div
          v-if="plan.priceOld && plan.priceOld !== plan.price"
          :class="['row-description', 'row-description--line-through']"
        >
          $ {{ numeral(plan.priceOld / 100).format('0,0[.]00') }} /
          {{ t('mo') }}
        </div>
      </template>
    </div>
    <div class="row">
      <div class="row-title">
        {{ plan.dataRetention }}
        {{ tc('month', plan.dataRetention) }}
      </div>
      <div class="row-description">
        {{ t('Data retention') }}
      </div>
    </div>

    <div class="row">
      <div class="row-title">
        {{ numeral(plan.eventsIncluded).format('0,0[.]00') }}
      </div>
      <div class="row-description">
        {{ t('events per month') }}
      </div>
    </div>

    <div class="row">
      <div v-if="plan.id === 'basic' || plan.id === 'free'">
        {{ t('Payment of overage not available') }}
      </div>
      <template v-else>
        <div class="row-title">$ {{ plan.overageCost }}</div>
        <div class="row-description">
          {{ t('per 1,000 events overage') }}
        </div>
      </template>
    </div>
    <div class="select-plan" v-if="showSelectPlanButton">
      <span v-if="plan.id === selectedPlanId" class="current-plan-text">{{
        t('Current Plan')
      }}</span>
      <ui-button
        v-else
        :text="t('Select Plan')"
        icon="fas fa-check"
        button-style="accent"
        @click="handlePlanSelect(plan)"
      ></ui-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import numeral from 'numeral';
import { useColors, useI18n } from '@/composables';
import type { BillingPlan } from '@/services/models';

withDefaults(
  defineProps<{
    plan: BillingPlan;
    selectedPlanId?: string;
    showSelectPlanButton?: boolean;
    handlePlanSelect?: (plan: BillingPlan) => void;
  }>(),
  {
    selectedPlanId: '',
    showSelectPlanButton: false,
  },
);

const { t, tc } = useI18n();
const colors = useColors();
</script>

<style lang="scss" scoped>
.item {
  width: 180px;
  min-width: 160px;
  border-radius: 8px;
  list-style-type: none;
  border: solid 2px var(--color-gray10);
  padding: 16px 0;
  &.item--allowToClick {
    cursor: pointer;
  }
  &.item--allowToClick:hover {
    box-shadow: 0 0 8px 0 var(--color-green20);
  }
}

.item_free,
.item_basic {
  &.item--allowToClick:hover,
  &.item--selected {
    border-color: var(--color-green);
  }

  .header-container {
    background-color: var(--color-green);
  }
}

[class*='item_team'] {
  &.item--allowToClick:hover,
  &.item--selected {
    border-color: var(--color-tangerine);
  }

  .header-container {
    background-color: var(--color-tangerine);
  }
}

[class*='item_professional'] {
  &.item--allowToClick:hover,
  &.item--selected {
    border-color: var(--color-grass);
  }

  .header-container {
    background-color: var(--color-grass);
  }
}

[class*='item_business'] {
  &.item--allowToClick:hover,
  &.item--selected {
    border-color: var(--color-blue);
  }

  .header-container {
    background-color: var(--color-blue);
  }
}

[class*='item_enterprise'] {
  &.item--allowToClick:hover,
  &.item--selected {
    border-color: var(--color-bluedark);
  }

  .header-container {
    background-color: var(--color-bluedark);
  }
}

.list-item {
  border-radius: 8px;
  flex: 1 1 0;
  list-style-type: none;
  border: solid 2px var(--color-gray10);
  padding: 16px 0;
  cursor: pointer;

  &:hover {
    box-shadow: 0 0 8px 0 var(--color-green20);
  }
}

.row {
  min-height: 52px;
  padding: 4px 16px;

  &:nth-child(even) {
    background-color: var(--color-gray5);
  }
}

.row__first {
  min-height: 108px;
}

.header-row {
  display: flex;
  justify-content: space-between;
  color: var(--color-white);
  margin-left: -2px;
}

.header-container {
  display: flex;
  align-items: center;

  &--name {
    min-height: 24px;
    border-radius: 8px;
    padding: 4px 8px;
  }

  &--selected {
    width: 24px;
    height: 24px;
    border-radius: 12px;
    padding: 0 6px;
  }
}

.name-text {
  font-size: 14px;
  line-height: 1.14;
  font-weight: bold;
}

.row-price {
  display: inline-block;
  width: 100%;
  text-align: start;
}

.row-price-value {
  font-size: 20px;
  font-weight: bold;
  color: var(--color-graphite);
  padding-right: 4px;
}

.row-price-unit {
  font-size: 20px;
  color: var(--color-gray40);
}

.row-title {
  display: inline-block;
  width: 100%;
  text-align: start;
  color: var(--color-graphite);
  font-size: 14px;
  font-weight: bold;
}

.row-description {
  display: inline-block;
  width: 100%;
  text-align: start;
  font-size: 12px;
  color: var(--color-graphite);
  line-height: 1.83;
  text-transform: lowercase;

  &--line-through {
    text-decoration: line-through;
  }
}

@media (max-width: 800px) {
  .list {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
  }

  .list-item {
    flex-basis: auto;
    flex-grow: 0;
    // width: 50% !important;
  }

  .list-wrapper {
    width: 50% !important;
  }
}

.current-plan-text {
  color: var(--color-green);
  font-size: 12px;
}

.select-plan {
  padding: 16px 16px 0 16px;
}
</style>
